import { userStrategyRef } from "@trader/shared/StrategiesApi"
import { useEffect, useState } from "react"
import { strategyEditor } from "../strategy-editor/editorPanel.ts"
import { setStrategy } from "../workspace/workspaceStore.ts"
import { strategyCatalog } from "./backtestClient.ts"
import { useMyStrategies } from "./myStrategies.ts"
import type { CatalogItem } from "./protocol.ts"
import { testerPanel } from "./testerPanel.ts"

const MINE = "My strategies"
type Group = typeof MINE | "Built-in" | "Community"
const GROUPS: ReadonlyArray<Group> = [MINE, "Built-in", "Community"]

type Catalog =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly items: ReadonlyArray<CatalogItem> }
  | { readonly status: "failed"; readonly message: string }

const matches = (query: string, ...texts: ReadonlyArray<string | undefined>) =>
  query === "" || texts.some((text) => (text ?? "").toLowerCase().includes(query))

/**
 * The strategies: the user's own (written in the strategy editor), then the
 * backtester's (TradingView's built-ins, then community scripts), searchable.
 * Picking one puts it on the active chart, replacing its strategy, and opens
 * the Strategy Tester. A user strategy goes on pinned to its latest version.
 */
export function StrategyBrowser({ onPicked, query }: {
  /** Lower-case search text. */
  readonly query: string
  readonly onPicked: () => void
}) {
  const [catalog, setCatalog] = useState<Catalog>({ status: "loading" })
  const [group, setGroup] = useState<Group | null>(null)
  const mine = useMyStrategies()

  useEffect(() => {
    let current = true
    strategyCatalog().then(
      (items) => current && setCatalog({ status: "ready", items }),
      (error: unknown) => current && setCatalog({ status: "failed", message: error instanceof Error ? error.message : String(error) })
    )
    return () => {
      current = false
    }
  }, [])

  const pick = (strategyId: string) => {
    setStrategy(strategyId)
    testerPanel.open()
    onPicked()
  }

  const showMine = group === null || group === MINE
  const mineItems = showMine && (mine.status === "ready" || mine.status === "loading")
    ? mine.items.filter((item) => matches(query, item.name))
    : []
  const results = catalog.status === "ready" && group !== MINE
    ? catalog.items
      .filter((item) => group === null || item.group === group)
      .filter((item) => matches(query, item.name, item.author))
    : []

  return (
    <div className="indicator-browser">
      <nav className="indicator-categories">
        <button type="button" aria-pressed={group === null} onClick={() => setGroup(null)}>All</button>
        {GROUPS.map((name) => (
          <button key={name} type="button" aria-pressed={group === name} onClick={() => setGroup(name)}>
            {name}
          </button>
        ))}
      </nav>
      <ul className="indicator-list">
        {showMine && (
          <li className="strategy-group-header">
            <span className="muted">{MINE}</span>
            <button
              type="button"
              className="link"
              onClick={() => {
                strategyEditor.create()
                onPicked()
              }}
            >
              New strategy
            </button>
          </li>
        )}
        {showMine && mine.status === "failed" && <li className="down">{mine.message}</li>}
        {showMine && mine.status === "ready" && mineItems.length === 0 && (
          <li className="muted">{query === "" ? "None yet: write one with New strategy." : "None of yours match."}</li>
        )}
        {mineItems.map((item) => (
          <li key={item.id} className="user-strategy">
            <button
              type="button"
              title={`Backtest version ${item.latestVersion} on the active chart (replaces its strategy)`}
              onClick={() => pick(userStrategyRef(item.id, item.latestVersion))}
            >
              <span>{item.name}</span>
              <span className="muted">v{item.latestVersion}</span>
            </button>
            <button
              type="button"
              className="link"
              title="Open in the strategy editor"
              onClick={() => {
                strategyEditor.edit(item.id)
                onPicked()
              }}
            >
              Edit
            </button>
          </li>
        ))}
        {group === null && <li className="strategy-group-header muted">Built-in and community</li>}
        {group !== MINE && catalog.status === "loading" && <li className="muted">Loading…</li>}
        {group !== MINE && catalog.status === "failed" && <li className="down">{catalog.message}</li>}
        {group !== MINE && catalog.status === "ready" && results.length === 0 && <li className="muted">No strategies match.</li>}
        {results.map((item) => (
          <li key={item.id}>
            <button type="button" title="Backtest on the active chart (replaces its strategy)" onClick={() => pick(item.id)}>
              <span>{item.name}</span>
              {item.author && <span className="muted">{item.author}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
